import React, {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaUsers,
  FaEdit,
  FaHome,
  FaUser,
  FaSyncAlt,
  FaChevronRight,
  FaChevronDown,
  FaLock,
  FaTrashAlt,
  FaUserSlash,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaWallet,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaChartPie,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import {
  mdl,
  formatINR,
  formatDateDMY,
  formatYearMonth,
  payRollSalaryApiKey,
} from "../dialog/payrollMonthlyShared";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const MdlSelectSession = React.lazy(() => import("../dialog/MdlSelectSession"));
const MdlEditPayRollStaffDetails = React.lazy(() =>
  import("../dialog/MdlEditPayRollStaffDetails")
);

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const REMOVE_SALARY_STEPS = [
  { id: 1, label: "Prepare removal inventory" },
  { id: 2, label: "Delete payments & components" },
  { id: 3, label: "Delete monthly salary records" },
  { id: 4, label: "Remove staff profile & rules" },
  { id: 5, label: "Complete" },
];

const EMPTY_REMOVE_UI = () => ({
  open: false,
  phase: "confirm", // loading | confirm | running | done | error
  step: 0,
  message: "",
  preview: null,
});

const ui = {
  pageBg: "#f5f7fb",
  surface: "#ffffff",
  border: "rgba(148, 163, 184, 0.32)",
  divider: "rgba(148, 163, 184, 0.22)",
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  radiusLg: 16,
  shadowSoft:
    "0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.04)",
};

//Created By Cursor
function staffInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

//Created By Cursor
function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("paid")) return "paid";
  if (s.includes("pending") || s.includes("draft")) return "pending";
  if (s.includes("process") || s.includes("approved")) return "process";
  if (s.includes("fail") || s.includes("cancel") || s.includes("revers"))
    return "danger";
  return "default";
}

//Created By Cursor
function monthPayProgress(row) {
  const net = Number(row.rounded_net ?? row.net_salary ?? 0);
  const paid = Number(row.total_paid ?? 0);
  if (net <= 0) {
    return {
      net,
      paid,
      balance: Math.max(0, net - paid),
      pct: paid > 0 ? 100 : 0,
    };
  }
  const pct = Math.max(0, Math.min(100, Math.round((paid / net) * 100)));
  return {
    net,
    paid,
    balance: Math.max(0, net - paid),
    pct,
  };
}

//Created By Cursor
const MonthlySalaryRow = memo(function MonthlySalaryRow({
  row,
  index,
  onOpen,
  onRemove,
  removing,
}) {
  const locked = Number(row.is_locked) === 1;
  const progress = monthPayProgress(row);
  const hasPaid = progress.paid > 0;
  const canRemove = !locked && !hasPaid && !removing;
  const tone = statusTone(row.status || row.status_code);

  const metrics = [
    { key: "net", label: "Net", value: formatINR(progress.net), tone: "net" },
    { key: "paid", label: "Paid", value: formatINR(progress.paid), tone: "paid" },
    {
      key: "bal",
      label: "Bal",
      value: formatINR(progress.balance),
      tone: "bal",
    },
    {
      key: "days",
      label: "Days",
      value: `${row.payable_days ?? "—"}/${row.working_days ?? "—"}`,
      tone: "days",
    },
    {
      key: "att",
      label: "P/A/L",
      value: `${row.staff_present ?? 0}/${row.staff_absent ?? 0}/${
        row.staff_leave ?? 0
      }`,
      tone: "att",
    },
  ];

  const removeTitle = locked
    ? "Locked month cannot be removed"
    : hasPaid
    ? "Cannot remove month with payments"
    : "Remove Month Salary";

  return (
    <div
      className="psd-month-row psd-month-row--compact"
      style={{ "--rn-i": index }}
    >
      <div className="psd-month-row__accent" aria-hidden />
      <button
        type="button"
        className="psd-month-row__open min-w-0"
        onClick={() => onOpen(row)}
      >
        <div className="psd-month-row__main min-w-0">
          <div className="psd-month-row__title-line">
            <span className="psd-month-row__month">
              {formatYearMonth(row.year_month)}
            </span>
            <span
              className={`psd-month-row__status psd-month-row__status--${tone}`}
            >
              {row.status || row.status_code || "—"}
            </span>
            {locked ? (
              <span className="psd-month-row__lock" title="Locked">
                <FaLock aria-hidden /> Locked
              </span>
            ) : null}
            <span className="psd-month-row__pct" title="Payment progress">
              {progress.pct}%
            </span>
          </div>
          <div
            className="psd-month-progress"
            role="progressbar"
            aria-valuenow={progress.pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Payment progress ${progress.pct}%`}
          >
            <span
              className={`psd-month-progress__bar psd-month-progress__bar--${
                progress.pct >= 100
                  ? "paid"
                  : progress.pct > 0
                  ? "partial"
                  : "empty"
              }`}
              style={{ width: `${progress.pct}%` }}
            />
          </div>
          <div className="psd-month-row__metrics">
            {metrics.map((m) => (
              <span
                key={m.key}
                className={`psd-month-metric psd-month-metric--${m.tone}`}
              >
                <em>{m.label}</em>
                <strong>{m.value}</strong>
              </span>
            ))}
          </div>
        </div>
      </button>
      <button
        type="button"
        className="psd-month-remove"
        onClick={(e) => {
          e.stopPropagation();
          if (canRemove) onRemove(row);
        }}
        disabled={!canRemove}
        title={removeTitle}
        aria-label="Remove Month Salary"
      >
        <span className="psd-month-remove__icon" aria-hidden>
          <FaTrashAlt />
        </span>
        <span className="psd-month-remove__text">Remove Month</span>
      </button>
      <button
        type="button"
        className="psd-month-row__go"
        onClick={() => onOpen(row)}
        title="View salary details"
        aria-label="View salary details"
      >
        <FaChevronRight aria-hidden />
      </button>
    </div>
  );
});

//Created By Cursor
function PayRollStaffDetail() {
  const history = useHistory();
  const location = useLocation();
  const { staff_id: staffIdParam } = useParams();
  const loggedIn = localStorage.getItem("token") != null;

  const querySessionId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("session_id") || "";
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [staff, setStaff] = useState(null);
  const [monthlySalaries, setMonthlySalaries] = useState([]);
  const [salariesLoading, setSalariesLoading] = useState(false);
  const [sessionId, setSessionId] = useState(querySessionId || "");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [removeUi, setRemoveUi] = useState(() => EMPTY_REMOVE_UI());
  const [removingMonthKey, setRemovingMonthKey] = useState("");
  const reqIdRef = useRef(0);
  const salaryReqRef = useRef(0);

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession();
    const current = mSession?.[0] || null;

    if (querySessionId) {
      setSessionId(querySessionId);
      if (current && String(current.session_id) === String(querySessionId)) {
        setSessionName(current.session_name || "");
        setSessionStatus(current.status || "");
        return;
      }
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) return;
      axios
        .get(`${API_BASE()}getSessionList.php`, {
          params: { school_code: schoolCode },
        })
        .then((res) => res.data)
        .then((payload) => {
          if (payload?.api_status !== "OK" || !Array.isArray(payload.data)) return;
          const found = payload.data.find(
            (s) => String(s.session_id) === String(querySessionId)
          );
          if (found) {
            setSessionName(found.session_name || "");
            setSessionStatus(found.status || "");
          }
        })
        .catch(() => {});
      return;
    }

    if (current) {
      setSessionId(current.session_id || "");
      setSessionName(current.session_name || "");
      setSessionStatus(current.status || "");
    }
  }, [querySessionId]);

  //Created By Cursor
  const fetchMonthlySalaries = useCallback(async () => {
    const staffId = staffIdParam;
    const activeSession = sessionId || querySessionId;
    if (!staffId || !activeSession) {
      setMonthlySalaries([]);
      return;
    }
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;

    const reqId = ++salaryReqRef.current;
    setSalariesLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE()}getPayRollStaffMonthlySalaries.php`,
        {
          params: {
            school_code: schoolCode,
            session_id: activeSession,
            staff_id: staffId,
          },
        }
      );
      if (reqId !== salaryReqRef.current) return;
      if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
        setMonthlySalaries(res.data.data);
      } else {
        setMonthlySalaries([]);
      }
    } catch (error) {
      if (reqId !== salaryReqRef.current) return;
      console.error("[PayRollStaffDetail] monthly salaries error:", error);
      setMonthlySalaries([]);
    } finally {
      if (reqId === salaryReqRef.current) setSalariesLoading(false);
    }
  }, [staffIdParam, sessionId, querySessionId]);

  //Created By Cursor
  const fetchDetails = useCallback(
    async ({ soft = false } = {}) => {
      const staffId = staffIdParam;
      const activeSession = sessionId || querySessionId;
      if (!staffId || !activeSession) {
        setLoading(false);
        setStaff(null);
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        return;
      }

      const reqId = ++reqIdRef.current;
      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const res = await axios.get(`${API_BASE()}getPayRollStaffDetails.php`, {
          params: {
            school_code: schoolCode,
            session_id: activeSession,
            staff_id: staffId,
          },
        });
        if (reqId !== reqIdRef.current) return;
        if (res.data?.api_status === "OK" && res.data?.data) {
          setStaff(res.data.data);
        } else {
          setStaff(null);
          myToast.notify_warning(
            res.data?.api_remark || "Staff payroll details not found."
          );
        }
      } catch (error) {
        if (reqId !== reqIdRef.current) return;
        console.error("[PayRollStaffDetail] load error:", error);
        setStaff(null);
        myToast.notify_warning("Failed to load staff details.");
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [staffIdParam, sessionId, querySessionId]
  );

  useEffect(() => {
    fetchDetails();
    fetchMonthlySalaries();
  }, [fetchDetails, fetchMonthlySalaries]);

  //Created By Cursor
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      fetchDetails({ soft: true }),
      fetchMonthlySalaries(),
    ]);
  }, [fetchDetails, fetchMonthlySalaries]);

  //Created By Cursor
  const handleSelectedSession = useCallback(
    (nextSessionId, nextSessionName, nextSessionStatus) => {
      const sid = nextSessionId || "";
      setSessionId(sid);
      setSessionName(nextSessionName || "");
      setSessionStatus(nextSessionStatus || "");
      setSessionDialogOpen(false);
      history.replace({
        pathname: `/AdminMain/PayRollStaffDetail/${encodeURIComponent(
          String(staffIdParam || "")
        )}`,
        search: sid ? `?session_id=${encodeURIComponent(String(sid))}` : "",
      });
    },
    [history, staffIdParam]
  );

  //Created By Cursor
  const handleEditSaved = useCallback(async () => {
    setEditOpen(false);
    await handleRefresh();
  }, [handleRefresh]);

  //Created By Cursor
  const goBackToList = useCallback(() => {
    const sid = sessionId || querySessionId;
    history.push({
      pathname: "/AdminMain/PayRollStaffList",
      search: sid ? `?session_id=${encodeURIComponent(String(sid))}` : "",
    });
  }, [history, sessionId, querySessionId]);

  //Created By Cursor
  const openMonthlySalary = useCallback(
    (row) => {
      const salaryKey = payRollSalaryApiKey(row);
      if (!salaryKey) return;
      const qs = new URLSearchParams();
      const sid = row.session_id || sessionId || querySessionId;
      if (sid != null) qs.set("session_id", String(sid));
      if (row.year_month) qs.set("year_month", String(row.year_month));
      const search = qs.toString();
      history.push({
        pathname: `/AdminMain/PayRollSalaryDetails/${encodeURIComponent(
          String(salaryKey)
        )}`,
        search: search ? `?${search}` : "",
        state: { staffRecord: { ...row, staff_name: staff?.staff_name } },
      });
    },
    [history, sessionId, querySessionId, staff]
  );

  //Created By Cursor
  const handleRemoveSalaryMonth = useCallback(
    async (row) => {
      const salaryKey = payRollSalaryApiKey(row);
      const activeSession = sessionId || querySessionId;
      const schoolCode = SessionManager.getSchoolCode();
      if (!salaryKey || !activeSession || !schoolCode) return;

      const monthLabel = formatYearMonth(row.year_month) || "this month";
      const ok = window.confirm(
        `Remove salary month "${monthLabel}" for ${
          staff?.staff_name || "this staff"
        }? This cannot be undone.`
      );
      if (!ok) return;

      setRemovingMonthKey(salaryKey);
      try {
        const res = await axios.get(
          `${API_BASE()}DeletePayRollStaffSalaryMonth.php`,
          {
            params: {
              school_code: schoolCode,
              session_id: activeSession,
              salary_id: salaryKey,
            },
          }
        );
        if (res.data?.api_status === "OK") {
          myToast.notify_success(
            res.data?.api_remark || "Salary month removed."
          );
          setMonthlySalaries((prev) =>
            prev.filter((r) => payRollSalaryApiKey(r) !== salaryKey)
          );
        } else {
          myToast.notify_warning(
            res.data?.api_remark || "Could not remove salary month."
          );
        }
      } catch (error) {
        console.error("[PayRollStaffDetail] remove month error:", error);
        myToast.notify_warning("Failed to remove salary month.");
      } finally {
        setRemovingMonthKey("");
      }
    },
    [sessionId, querySessionId, staff]
  );

  //Created By Cursor
  const closeRemoveProcess = useCallback(() => {
    if (removeUi.phase === "running" || removeUi.phase === "loading") return;
    const shouldLeave = removeUi.phase === "done";
    setRemoveUi(EMPTY_REMOVE_UI());
    if (shouldLeave) goBackToList();
  }, [removeUi.phase, goBackToList]);

  //Created By Cursor
  const openRemoveFromSalarySystem = useCallback(async () => {
    const staffId = staffIdParam;
    const activeSession = sessionId || querySessionId;
    const schoolCode = SessionManager.getSchoolCode();
    if (!staffId || !activeSession || !schoolCode) {
      myToast.notify_warning("Staff or session is missing.");
      return;
    }

    setRemoveUi({
      open: true,
      phase: "loading",
      step: 0,
      message: "Loading payroll records to remove…",
      preview: null,
    });

    let preview = null;
    try {
      const previewRes = await axios.get(
        `${API_BASE()}RemoveStaffFromSalarySystem.php`,
        {
          params: {
            school_code: schoolCode,
            session_id: activeSession,
            staff_id: staffId,
            preview: 1,
          },
        }
      );
      if (
        previewRes.data?.api_status === "OK" &&
        previewRes.data?.data &&
        typeof previewRes.data.data === "object"
      ) {
        preview = previewRes.data.data;
      }
    } catch (previewErr) {
      console.error("[PayRollStaffDetail] remove preview error:", previewErr);
    }

    if (!preview) {
      const months = (monthlySalaries || [])
        .map((r) => r.year_month)
        .filter(Boolean);
      const monthCount = monthlySalaries?.length || 0;
      const items = ["Staff payroll profile (1)"];
      if (monthCount > 0) {
        items.push(`Monthly salary records (${monthCount})`);
        if (months.length) items.push(`Months: ${months.join(", ")}`);
      }
      items.push(
        "Salary rules / opening balance / components / payments (if any)"
      );
      preview = {
        items,
        total_records: 1 + monthCount,
        year_months: months,
        staff_details: 1,
        monthly_salaries: monthCount,
        salary_rules: 0,
        opening_balance: 0,
        salary_components: 0,
        salary_payments: 0,
      };
    }

    const items = Array.isArray(preview.items) ? preview.items : [];
    if (!items.length || Number(preview.total_records) <= 0) {
      setRemoveUi(EMPTY_REMOVE_UI());
      myToast.notify_warning("No payroll records found to remove.");
      return;
    }

    setRemoveUi({
      open: true,
      phase: "confirm",
      step: 0,
      message: "",
      preview,
    });
  }, [staffIdParam, sessionId, querySessionId, monthlySalaries]);

  //Created By Cursor
  const runRemoveFromSalarySystem = useCallback(async () => {
    const staffId = staffIdParam;
    const activeSession = sessionId || querySessionId;
    const schoolCode = SessionManager.getSchoolCode();
    if (!staffId || !activeSession || !schoolCode) return;

    const preview = removeUi.preview;
    setRemoveUi((prev) => ({
      ...prev,
      open: true,
      phase: "running",
      step: 1,
      message: "Preparing permanent removal…",
      preview,
    }));

    // Let the first step paint before kicking the API
    await new Promise((r) => setTimeout(r, 180));

    try {
      setRemoveUi((prev) => ({
        ...prev,
        step: 2,
        message: "Deleting payments and salary components…",
      }));

      const res = await axios.get(
        `${API_BASE()}RemoveStaffFromSalarySystem.php`,
        {
          params: {
            school_code: schoolCode,
            session_id: activeSession,
            staff_id: staffId,
          },
        }
      );

      const payload = res?.data;
      if (!payload || typeof payload !== "object") {
        setRemoveUi((prev) => ({
          ...prev,
          phase: "error",
          step: 2,
          message:
            "Server returned an invalid response. Redeploy RemoveStaffFromSalarySystem.php.",
        }));
        myToast.notify_warning("Invalid remove API response.");
        return;
      }

      if (payload.api_status !== "OK") {
        const remark =
          payload.api_remark || "Could not remove staff from salary system.";
        setRemoveUi((prev) => ({
          ...prev,
          phase: "error",
          step: 2,
          message: remark,
        }));
        myToast.notify_warning(remark);
        return;
      }

      setRemoveUi((prev) => ({
        ...prev,
        step: 3,
        message: "Deleting monthly salary records…",
      }));
      await new Promise((r) => setTimeout(r, 220));

      setRemoveUi((prev) => ({
        ...prev,
        step: 4,
        message: "Removing staff payroll profile and rules…",
      }));
      await new Promise((r) => setTimeout(r, 220));

      setStaff(null);
      setMonthlySalaries([]);
      setRemoveUi((prev) => ({
        ...prev,
        phase: "done",
        step: 5,
        message:
          payload.api_remark ||
          "All payroll records for this staff were permanently removed.",
      }));
      myToast.notify_success(
        payload.api_remark || "Staff removed from salary system."
      );
    } catch (error) {
      console.error("[PayRollStaffDetail] remove staff error:", error);
      const serverRemark =
        error?.response?.data?.api_remark ||
        error?.message ||
        "Failed to remove staff from salary system. Please try again.";
      setRemoveUi((prev) => ({
        ...prev,
        phase: "error",
        step: 2,
        message: serverRemark,
      }));
      myToast.notify_warning(serverRemark);
    }
  }, [staffIdParam, sessionId, querySessionId, removeUi.preview]);

  const dashboard = useMemo(() => {
    let netTotal = 0;
    let paidTotal = 0;
    let paidMonths = 0;
    let pendingMonths = 0;
    let processMonths = 0;
    let lockedMonths = 0;

    monthlySalaries.forEach((row) => {
      const p = monthPayProgress(row);
      netTotal += p.net;
      paidTotal += p.paid;
      const tone = statusTone(row.status || row.status_code);
      if (tone === "paid" || p.pct >= 100) paidMonths += 1;
      else if (tone === "process") processMonths += 1;
      else pendingMonths += 1;
      if (Number(row.is_locked) === 1) lockedMonths += 1;
    });

    const balanceTotal = Math.max(0, netTotal - paidTotal);
    const payPct =
      netTotal > 0
        ? Math.max(0, Math.min(100, Math.round((paidTotal / netTotal) * 100)))
        : 0;
    const monthCount = monthlySalaries.length;
    const monthPct =
      monthCount > 0 ? Math.round((paidMonths / monthCount) * 100) : 0;

    return {
      monthCount,
      netTotal,
      paidTotal,
      balanceTotal,
      payPct,
      monthPct,
      paidMonths,
      pendingMonths,
      processMonths,
      lockedMonths,
    };
  }, [monthlySalaries]);

  if (!loggedIn) return <Redirect to="/Login" />;

  const salary = staff?.basic_salary ?? staff?.salary;
  const role = staff?.staff_role || staff?.designation || "";
  const department = staff?.department_name || "";
  const showDepartment =
    Boolean(department) &&
    department.trim().toLowerCase() !== String(role).trim().toLowerCase();
  const sessionLabel = sessionName || sessionId || "Session";

  const profileChips = staff
    ? [
        role
          ? { key: "role", label: role, tone: "role" }
          : null,
        showDepartment
          ? { key: "dept", label: department, tone: "dept" }
          : null,
        {
          key: "salary",
          label: `Basic ${formatINR(salary)}`,
          tone: "salary",
        },
        staff.salary_type
          ? { key: "type", label: staff.salary_type, tone: "type" }
          : null,
        {
          key: "joined",
          label: `Joined ${formatDateDMY(staff.joining_date)}`,
          tone: "joined",
        },
        staff.week_off
          ? { key: "off", label: `Off ${staff.week_off}`, tone: "off" }
          : null,
        staff.working_hour
          ? {
              key: "hours",
              label: `${staff.working_hour}h/day`,
              tone: "hours",
            }
          : null,
      ].filter(Boolean)
    : [];

  return (
    <div
      className="content payroll-page sm-page rn-page psd-page"
      style={{
        background: ui.pageBg,
        fontFamily: mdl.font,
        color: ui.text,
        minHeight: "100%",
      }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ borderBottom: `1px solid ${ui.divider}`, paddingBottom: 6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            aria-label="Back to staff list"
            onClick={goBackToList}
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: COLORS.primary_color_shade5,
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ fontWeight: 700, color: ui.textSoft }}>
                Payroll / Staff Salary Dashboard
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div style={{ padding: "12px 16px 24px", maxWidth: 1180, margin: "0 auto" }}>
        <div
          style={{
            background: ui.surface,
            border: `1px solid ${ui.border}`,
            borderRadius: ui.radiusLg,
            boxShadow: ui.shadowSoft,
            overflow: "hidden",
          }}
        >
          <header
            className="sm-hero"
            style={{ "--sm-hero-bg": HERO_GRADIENT, borderRadius: 0 }}
          >
            <div className="sm-hero__brand">
              <div className="sm-hero__icon" aria-hidden>
                <FaChartPie />
              </div>
              <div className="min-w-0">
                <h1 className="sm-hero__title">
                  {staff?.staff_name
                    ? `${staff.staff_name} · Salary Dashboard`
                    : "Staff Salary Dashboard"}
                </h1>
                <p className="sm-hero__sub">
                  {sessionLabel}
                  {role ? ` · ${role}` : ""}
                </p>
              </div>
            </div>
            <div className="sm-hero__tools">
              <button
                type="button"
                className="psl-session-pill"
                onClick={() => setSessionDialogOpen(true)}
                title="Change session"
              >
                <span
                  className={`psl-session-pill__dot${
                    sessionStatus && /current|active/i.test(sessionStatus)
                      ? " is-active"
                      : ""
                  }`}
                  aria-hidden
                />
                <span className="psl-session-pill__text text-truncate">
                  {sessionName || "Choose session…"}
                </span>
                {sessionStatus ? (
                  <span className="psl-session-pill__status">{sessionStatus}</span>
                ) : null}
                <FaChevronDown aria-hidden />
              </button>
              <button
                type="button"
                className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
                onClick={handleRefresh}
                disabled={loading || refreshing || !sessionId}
                title="Refresh"
                aria-label="Refresh"
              >
                <FaSyncAlt aria-hidden />
              </button>
              <button
                type="button"
                className="psd-edit-btn"
                onClick={() => setEditOpen(true)}
                disabled={loading || !staff}
              >
                <FaEdit aria-hidden />
                <span>Edit</span>
              </button>
            </div>
          </header>

          <div className="rn-content" style={{ background: "#fff" }}>
            {loading ? (
              <div className="rn-loading">
                <MyLoader />
              </div>
            ) : !staff ? (
              <div className="sm-empty">
                <FaUsers className="rn-empty-icon" aria-hidden />
                <p className="sm-empty__title">Payroll details not found</p>
                <p className="sm-empty__hint">
                  This staff is not available in the selected session salary records.
                </p>
                <button
                  type="button"
                  className="btn lift payroll-cta"
                  style={{
                    marginTop: 12,
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                  onClick={goBackToList}
                >
                  Back to staff list
                </button>
              </div>
            ) : (
              <>
                <section className="psd-profile psd-profile--line" aria-label="Staff details">
                  <div className="psd-avatar" aria-hidden>
                    {staff.staff_image_path ? (
                      <img src={staff.staff_image_path} alt="" />
                    ) : (
                      <span>{staffInitials(staff.staff_name)}</span>
                    )}
                  </div>
                  <div className="psd-profile__body min-w-0">
                    <h2 className="psd-profile__name">
                      {staff.staff_name || "—"}
                    </h2>
                    <div className="psd-profile__chips">
                      {profileChips.map((chip) => (
                        <span
                          key={chip.key}
                          className={`psd-chip psd-chip--${chip.tone}`}
                        >
                          {chip.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="psd-dash" aria-label="Salary overview">
                  <div className="psd-kpi">
                    <div className="psd-kpi__card psd-kpi__card--months">
                      <span className="psd-kpi__icon" aria-hidden>
                        <FaCalendarAlt />
                      </span>
                      <div className="psd-kpi__text">
                        <span className="psd-kpi__label">Months</span>
                        <strong className="psd-kpi__value">
                          {dashboard.monthCount}
                        </strong>
                      </div>
                    </div>
                    <div className="psd-kpi__card psd-kpi__card--salary">
                      <span className="psd-kpi__icon" aria-hidden>
                        <FaWallet />
                      </span>
                      <div className="psd-kpi__text">
                        <span className="psd-kpi__label">Net total</span>
                        <strong className="psd-kpi__value">
                          {formatINR(dashboard.netTotal)}
                        </strong>
                      </div>
                    </div>
                    <div className="psd-kpi__card psd-kpi__card--paid">
                      <span className="psd-kpi__icon" aria-hidden>
                        <FaMoneyBillWave />
                      </span>
                      <div className="psd-kpi__text">
                        <span className="psd-kpi__label">Paid</span>
                        <strong className="psd-kpi__value">
                          {formatINR(dashboard.paidTotal)}
                        </strong>
                      </div>
                    </div>
                    <div className="psd-kpi__card psd-kpi__card--balance">
                      <span className="psd-kpi__icon" aria-hidden>
                        <FaChartPie />
                      </span>
                      <div className="psd-kpi__text">
                        <span className="psd-kpi__label">Balance due</span>
                        <strong className="psd-kpi__value">
                          {formatINR(dashboard.balanceTotal)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="psd-progress-card">
                    <div className="psd-progress-card__head">
                      <div>
                        <h3 className="psd-progress-card__title">
                          Payment progress
                        </h3>
                        <p className="psd-progress-card__sub">
                          Across {dashboard.monthCount} salary month
                          {dashboard.monthCount === 1 ? "" : "s"} this session
                        </p>
                      </div>
                      <span className="psd-progress-card__pct">
                        {dashboard.payPct}%
                      </span>
                    </div>
                    <div
                      className="psd-progress-track"
                      role="progressbar"
                      aria-valuenow={dashboard.payPct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Overall payment progress"
                    >
                      <span
                        className="psd-progress-track__fill"
                        style={{ width: `${dashboard.payPct}%` }}
                      />
                    </div>
                    <div className="psd-progress-meta">
                      <span>
                        <i className="psd-dot psd-dot--paid" aria-hidden />
                        Paid {dashboard.paidMonths}
                      </span>
                      <span>
                        <i className="psd-dot psd-dot--process" aria-hidden />
                        Processing {dashboard.processMonths}
                      </span>
                      <span>
                        <i className="psd-dot psd-dot--pending" aria-hidden />
                        Pending {dashboard.pendingMonths}
                      </span>
                      <span>
                        <i className="psd-dot psd-dot--lock" aria-hidden />
                        Locked {dashboard.lockedMonths}
                      </span>
                    </div>
                    <div className="psd-month-completion">
                      <div className="psd-month-completion__label">
                        <span>Months fully paid</span>
                        <strong>
                          {dashboard.paidMonths}/{dashboard.monthCount || 0}
                        </strong>
                      </div>
                      <div
                        className="psd-progress-track psd-progress-track--thin"
                        role="progressbar"
                        aria-valuenow={dashboard.monthPct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <span
                          className="psd-progress-track__fill psd-progress-track__fill--months"
                          style={{ width: `${dashboard.monthPct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </section>

                <section className="psd-panel">
                  <div className="psd-panel__head">
                    <h3 className="psd-panel__title">Monthly salaries</h3>
                    <span className="psd-panel__count">
                      {monthlySalaries.length} month
                      {monthlySalaries.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {salariesLoading ? (
                    <div className="rn-loading" style={{ padding: "24px 0" }}>
                      <MyLoader />
                    </div>
                  ) : monthlySalaries.length === 0 ? (
                    <div className="psd-month-empty">
                      No monthly salary records for this staff in the selected
                      session.
                    </div>
                  ) : (
                    <div className="psd-month-list">
                      {monthlySalaries.map((row, index) => {
                        const key =
                          payRollSalaryApiKey(row) ||
                          `${row.year_month}-${index}`;
                        return (
                          <MonthlySalaryRow
                            key={key}
                            row={row}
                            index={index}
                            onOpen={openMonthlySalary}
                            onRemove={handleRemoveSalaryMonth}
                            removing={removingMonthKey === payRollSalaryApiKey(row)}
                          />
                        );
                      })}
                    </div>
                  )}
                </section>

                <div className="psd-danger-zone">
                  <div className="psd-danger-zone__card">
                    <div className="psd-danger-zone__copy">
                      <strong>Danger zone</strong>
                      <span>
                        Permanently remove this staff and all related payroll
                        records for the selected session.
                      </span>
                      <span className="psd-danger-zone__note">
                        Staff primary details are still unchanged
                      </span>
                    </div>
                    <button
                      type="button"
                      className="psd-remove-system-btn"
                      onClick={openRemoveFromSalarySystem}
                      disabled={
                        removeUi.open ||
                        loading ||
                        removeUi.phase === "running" ||
                        removeUi.phase === "loading"
                      }
                    >
                      <span className="psd-remove-system-btn__icon" aria-hidden>
                        <FaUserSlash />
                      </span>
                      <span className="psd-remove-system-btn__text">
                        <span className="psd-remove-system-btn__title">
                          Remove From Salary System
                        </span>
                        <span className="psd-remove-system-btn__hint">
                          Hard delete · cannot undo
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {removeUi.open ? (
        <div
          className="rn-restore-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="psd-remove-title"
        >
          <div className="rn-restore-panel rn-restore-panel--remove">
            <div className="rn-restore-panel__head">
              <div
                className="rn-restore-panel__icon rn-restore-panel__icon--remove"
                aria-hidden
              >
                {removeUi.phase === "done" ? (
                  <FaCheckCircle />
                ) : removeUi.phase === "error" ? (
                  <FaTimesCircle />
                ) : (
                  <FaUserSlash />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="rn-restore-panel__title" id="psd-remove-title">
                  {removeUi.phase === "loading"
                    ? "Preparing removal…"
                    : removeUi.phase === "confirm"
                    ? "Remove from salary system?"
                    : removeUi.phase === "running"
                    ? "Removing payroll records…"
                    : removeUi.phase === "done"
                    ? "Removal complete"
                    : "Removal failed"}
                </h3>
                <p className="rn-restore-panel__sub">
                  {removeUi.phase === "confirm"
                    ? `Permanently delete all payroll data for ${
                        staff?.staff_name || "this staff"
                      }.`
                    : removeUi.phase === "loading"
                    ? "Checking which payroll records will be deleted…"
                    : removeUi.message}
                </p>
              </div>
            </div>

            {removeUi.preview && removeUi.phase !== "loading" ? (
              <div className="rn-restore-meta">
                <div className="rn-restore-meta__item">
                  <span className="rn-restore-meta__label">Staff</span>
                  <span className="rn-restore-meta__value">
                    {staff?.staff_name || staffIdParam || "—"}
                  </span>
                </div>
                <div className="rn-restore-meta__item">
                  <span className="rn-restore-meta__label">Total records</span>
                  <span className="rn-restore-meta__value">
                    {removeUi.preview.total_records ?? "—"}
                  </span>
                </div>
                <div className="rn-restore-meta__item">
                  <span className="rn-restore-meta__label">Months</span>
                  <span className="rn-restore-meta__value">
                    {removeUi.preview.monthly_salaries ?? 0}
                  </span>
                </div>
                <div className="rn-restore-meta__item">
                  <span className="rn-restore-meta__label">Payments</span>
                  <span className="rn-restore-meta__value">
                    {removeUi.preview.salary_payments ?? 0}
                  </span>
                </div>
              </div>
            ) : null}

            {removeUi.phase === "confirm" &&
            Array.isArray(removeUi.preview?.items) &&
            removeUi.preview.items.length ? (
              <ul className="psd-remove-inventory">
                {removeUi.preview.items.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}

            {removeUi.phase === "confirm" ? (
              <div className="rn-restore-warn">
                <FaExclamationTriangle aria-hidden />
                <span>
                  This is a hard delete and cannot be undone. Monthly salaries,
                  payments, components, rules, and the staff payroll profile will
                  all be removed.
                </span>
              </div>
            ) : null}

            {removeUi.phase !== "loading" ? (
              <ol className="rn-restore-steps">
                {REMOVE_SALARY_STEPS.map((step) => {
                  const active = removeUi.step === step.id;
                  const done =
                    removeUi.phase === "done" || removeUi.step > step.id;
                  const failed =
                    removeUi.phase === "error" && removeUi.step === step.id;
                  return (
                    <li
                      key={step.id}
                      className={`rn-restore-step${
                        done ? " rn-restore-step--done" : ""
                      }${active ? " rn-restore-step--active" : ""}${
                        failed ? " rn-restore-step--error" : ""
                      }`}
                    >
                      <span className="rn-restore-step__index" aria-hidden>
                        {done && !failed ? <FaCheckCircle /> : step.id}
                      </span>
                      <span className="rn-restore-step__label">{step.label}</span>
                      {active && removeUi.phase === "running" ? (
                        <span className="rn-restore-step__pulse" aria-hidden />
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="rn-loading" style={{ padding: "20px 0" }}>
                <MyLoader />
              </div>
            )}

            <div className="rn-restore-actions">
              {removeUi.phase === "confirm" ? (
                <>
                  <button
                    type="button"
                    className="rn-btn rn-btn--ghost"
                    onClick={closeRemoveProcess}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rn-btn rn-btn--remove"
                    onClick={runRemoveFromSalarySystem}
                  >
                    <span className="rn-btn__icon" aria-hidden>
                      <FaUserSlash />
                    </span>
                    <span className="rn-btn__text">
                      <span className="rn-btn__title">Start Removal</span>
                      <span className="rn-btn__hint">Hard delete now</span>
                    </span>
                  </button>
                </>
              ) : removeUi.phase === "loading" ? null : (
                <button
                  type="button"
                  className="rn-btn rn-btn--solid"
                  onClick={closeRemoveProcess}
                  disabled={removeUi.phase === "running"}
                >
                  <span className="rn-btn__text">
                    <span className="rn-btn__title">
                      {removeUi.phase === "running"
                        ? "Please wait…"
                        : removeUi.phase === "done"
                        ? "Back to staff list"
                        : "Close"}
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {sessionDialogOpen ? (
        <Suspense fallback={null}>
          <MdlSelectSession
            action={() => setSessionDialogOpen(false)}
            action2={handleSelectedSession}
          />
        </Suspense>
      ) : null}

      {editOpen && staff ? (
        <Suspense fallback={null}>
          <MdlEditPayRollStaffDetails
            show={editOpen}
            actionClose={() => setEditOpen(false)}
            staffRecord={staff}
            sessionId={sessionId || querySessionId}
            onSaved={handleEditSaved}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

export default PayRollStaffDetail;
